import sum from 'lodash/sum';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import {
  CustomerInfo,
  DEFAULT_ESTIMATE,
  DrawingShape,
  Estimate,
  PricedProduct,
  ProductCatalogMap,
  ProductNote,
  SelectedItemMap,
  createSerializableDrawingShape,
} from '~/core/models';
import {
  cloneCornerMap,
  getProductDescription,
  isActualProduct,
} from '~/core/utils';
import { useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { selectCustomCutouts } from '~/redux/slices/cutout';
import { selectIsApplySameEdgeActive } from '~/redux/slices/edge';
import { selectSelectedMaterial } from '~/redux/slices/material';
import { lineItemsFromPricedProducts } from '~/redux/slices/pricedProducts/pricedProducts.utils';
import { selectSelectedTearOuts } from '~/redux/slices/tearOut';
import { selectZipcode } from '~/redux/slices/zipcode';
import {
  useChipMinimizerContext,
  useDrawingShapeContext,
  useFaucetContext,
  useOptionalProductContext,
  usePricedProductsContext,
  useProductDetailsContext,
} from '~contexts';

/**
 * LoadEstimateKeys must have either code OR (estimateNumber and email)
 * @param code is the ShortGuid for the estimate
 * @param estimateNumber is the estimate number to fetch
 * @param email is the customer email associated with the estimate Number
 */
export interface LoadEstimateKeys {
  code?: string;
  estimateNumber?: string;
  email?: string;
}

type useEstimateReturn = {
  updateEstimateFromDesign: (customerInfo?: Partial<CustomerInfo>) => Estimate;
};

/**
 *  Provides access to all the contexts for building the save-able estimate
 */
export const useEstimate = (): useEstimateReturn => {
  const { t } = useTranslation();

  const zipcode = useAppSelector(selectZipcode);
  const applySameEdge = useAppSelector(selectIsApplySameEdgeActive);
  const selectedColor = useAppSelector(selectSelectedColor);
  const selectedMaterial = useAppSelector(selectSelectedMaterial);
  const selectedCutouts = useAppSelector(selectCustomCutouts).selected;
  const selectedTearOuts = useAppSelector(selectSelectedTearOuts);

  const selectedStore = useSelectedStore();
  const {
    getters: { drawingShapes },
  } = useDrawingShapeContext();

  const { pricedProducts } = usePricedProductsContext();
  // we handle sinks, radii, and diagonal corners differently
  const { getRestorableFaucets } = useFaucetContext();
  const { makeRestorableChipMinimizers } = useChipMinimizerContext();
  const { makeRestorableOptionalProducts, makeOptionalProductNotes } =
    useOptionalProductContext();

  const { productCatalog } = useProductDetailsContext();

  /**
   * create a new Estimate object using current design selections
   * TODO: needs update from EE2
   */
  const updateEstimateFromDesign = useCallback(
    (customerInfo?: Partial<CustomerInfo>): Estimate => ({
      ...DEFAULT_ESTIMATE,
      customerInfo,
      productNotes: [
        ...makeOptionalProductNotes(),
        ...pricedProducts.reduce((notes, product) => {
          if (
            isActualProduct(product.productId) &&
            product.quantity !== product.adjustedQty
          ) {
            return [
              ...notes,
              {
                productDescription: getProductDescription(product),
                note: t('COMMON.PROMOTION_NOTE', { ...product }),
              },
            ];
          }

          return notes;
        }, [] as ProductNote[]),
      ],
      drawingShapes: drawingShapes.map((shape: DrawingShape) =>
        createSerializableDrawingShape({
          skeleton: shape.skeleton,
          cornerMap: cloneCornerMap(shape.cornerMap),
          drawingEdges: shape.drawingEdges,
          edge: shape.edge,
          appliances: shape.appliances,
          removedElementsHistory: shape.removedElementsHistory,
          label: shape.label,
          counterLabel: shape.counterLabel,
          establishedLetterStartIndex: shape.establishedLetterStartIndex,
        })
      ),
      color: selectedColor,
      zipCode: zipcode,
      storeId: selectedStore?.companyId ?? undefined,
      materialId: selectedMaterial?.id ?? null,
      materialDesc: selectedMaterial?.description ?? null,
      qteGrpID: selectedColor?.qteGrpID ?? null,
      applySameEdge: applySameEdge,
      cutOuts: Object.entries(selectedCutouts).reduce<SelectedItemMap>(
        (cutouts, [id, cutoutsCounter]) => {
          const numericId = parseInt(id);

          if (!isNaN(numericId)) {
            cutouts[numericId] = {
              quantity: sum(Object.values(cutoutsCounter)),
              isSelected: true,
              isManuallyChanged: false,
            };
          }

          return cutouts;
        },
        {}
      ),
      tearOuts: Object.entries(selectedTearOuts).reduce<SelectedItemMap>(
        (tearOuts, [productId, qty]) => {
          const id = parseInt(productId);

          return {
            ...tearOuts,
            [id]: { quantity: qty, isSelected: true, isManuallyChanged: false },
          };
        },
        {}
      ),
      chipMinimizers: makeRestorableChipMinimizers(),
      optionalProducts: makeRestorableOptionalProducts(),
      productCatalog: minimizeProductCatalog(pricedProducts, productCatalog),
      pricedProducts: [...pricedProducts],
      faucets: getRestorableFaucets(),
      lineItems: lineItemsFromPricedProducts(pricedProducts),
    }),
    [
      makeOptionalProductNotes,
      pricedProducts,
      drawingShapes,
      selectedColor,
      zipcode,
      selectedStore?.companyId,
      selectedMaterial?.id,
      selectedMaterial?.description,
      applySameEdge,
      selectedCutouts,
      selectedTearOuts,
      makeRestorableChipMinimizers,
      makeRestorableOptionalProducts,
      productCatalog,
      getRestorableFaucets,
      t,
    ]
  );

  const minimizeProductCatalog = (
    includeProducts: PricedProduct[],
    fullCatalog: ProductCatalogMap
  ): ProductCatalogMap =>
    includeProducts.reduce<ProductCatalogMap>((acc, product) => {
      const foundProduct = fullCatalog[product.productId];

      if (foundProduct) {
        acc[product.productId] = { ...foundProduct };
      }

      return acc;
    }, {});

  return {
    updateEstimateFromDesign,
  };
};
